import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { NotificationRail } from 'components/Notifications/desktop/NotificationRail'
import { NotificationListColumn } from 'components/Notifications/desktop/NotificationListColumn'
import { NotificationPreviewColumn } from 'components/Notifications/desktop/NotificationPreviewColumn'
import {
  handleFetchInbox,
  handleFetchUnread,
  handleFetchUnreadCount,
  handleMarkSeen,
} from 'redux/actions/notifications'
import {
  notificationsInboxSelector,
  notificationsUnreadSelector,
  unreadCountSelector,
} from 'redux/selectors/notifications'
import { isRtlSelector } from 'redux/selectors/general'
import { notifShadows, notifTokens } from 'services/notifications/designTokens'
import { handleNotificationClick } from 'services/notifications/handleNotificationClick'
import { INotification } from 'services/notifications/types'
import { getDefaultNotificationTab, NotificationCenterTab } from 'services/notifications/defaultNotificationTab'
import useLocaleText from 'components/useLocaleText'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

// Column 1-2-3 shell (spec §2) — 1240×780 centred, backdrop scrim, not full-screen even on a 4K
// monitor. Below the 1180px breakpoint NotificationBellButton renders NotificationCenterPanel
// (the tablet popover) instead of this.
export const NotificationDesktopModal: React.FC<IProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [tab, setTab] = useState<NotificationCenterTab>('unread')
  const [serviceFilter, setServiceFilter] = useState<string | null>(null)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [isMarkAllConfirmOpen, setIsMarkAllConfirmOpen] = useState(false)
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false)
  const didUserChooseTabRef = React.useRef(false)

  const inbox = useSelector(notificationsInboxSelector)
  const unread = useSelector(notificationsUnreadSelector)
  const unreadCount = useSelector(unreadCountSelector)
  const isRtl = useSelector(isRtlSelector)
  const active = tab === 'all' ? inbox : unread
  const t_all_caught_up = useLocaleText('all_caught_up')
  const mark_all_seen = useLocaleText('mark_all_seen')
  const mark_all_seen_desc = useLocaleText('mark_all_seen_desc')
  const yes = useLocaleText('yes')
  const no = useLocaleText('no')

  // Refetch both lists every time the modal opens, not just the first time — `hasLoadedOnce`
  // previously gated this, so once loaded in a session the Center never refreshed again on
  // reopen, silently going stale (a push notification received while the Center was closed —
  // it only lands in local toast state, see usePushNotificationMessages — would never actually
  // appear here). Existing items stay visible while the refetch is in flight (SET_LIST_LOADING
  // only flips `isLoading`, it doesn't clear `items`), so this doesn't introduce a loading flash.
  useEffect(() => {
    if (!isOpen) {
      didUserChooseTabRef.current = false
      return
    }
    setTab(getDefaultNotificationTab(unreadCount))
    dispatch(handleFetchInbox())
    dispatch(handleFetchUnread())
    Promise.resolve(dispatch(handleFetchUnreadCount())).then((count) => {
      if (typeof count === 'number' && !didUserChooseTabRef.current) {
        setTab(getDefaultNotificationTab(count))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Esc closes; body scroll is locked while open. Backdrop click closes from the overlay below,
  // while clicks inside the modal content stop propagation.
  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      // ConfirmationPopup has its own Escape listener on `document` (to cancel just itself) — both
      // fire on the same keypress, so without this guard Escape closed the confirm popup *and* the
      // whole notification modal underneath it in one press.
      if (e.key === 'Escape' && !isMarkAllConfirmOpen) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, onClose, isMarkAllConfirmOpen])

  // Scoped to the active tab's own list, not always `inbox` — otherwise the rail shows "Tasks: 5"
  // (from the full inbox) while on the Unread tab, where only e.g. 1 of those 5 is actually
  // unread; clicking the chip then filters `active.items` (unread) and looks like it shows
  // nothing, because the count you clicked never matched what's actually filterable right now.
  const serviceCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    active.items.forEach((n) => {
      counts[n.service] = (counts[n.service] || 0) + 1
    })
    return counts
  }, [active.items])

  const filteredItems = useMemo(
    () => (serviceFilter ? active.items.filter((n) => n.service === serviceFilter) : active.items),
    [active.items, serviceFilter]
  )

  // This component never unmounts on close (it just returns null), so `serviceFilter` survives
  // across close/reopen. If the filtered service's count just dropped to 0 — e.g. the user read the
  // one unread comment while a "Comments" filter was active — the rail correctly greys that chip
  // out, but the stale filter would keep silently filtering the list to nothing instead of falling
  // back to showing everything. Clear it once it no longer matches anything filterable.
  useEffect(() => {
    if (serviceFilter && !serviceCounts[serviceFilter]) {
      setServiceFilter(null)
    }
  }, [serviceFilter, serviceCounts])

  // Keep something previewed whenever the filtered list is non-empty and nothing valid is selected.
  useEffect(() => {
    if (!filteredItems.length) {
      setSelectedKey(null)
      return
    }
    if (!filteredItems.some((n) => n.chrono_key === selectedKey)) {
      setSelectedKey(filteredItems[0].chrono_key)
    }
  }, [filteredItems, selectedKey])

  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  const selectedNotification = filteredItems.find((n) => n.chrono_key === selectedKey) ?? null

  const onSelectRow = (notification: INotification) => {
    setSelectedKey(notification.chrono_key)
  }

  const onOpenNotification = (notification: INotification) => {
    onClose()
    handleNotificationClick(notification, navigate, {
      dispatch,
      markSeen: (routedNotification) => {
        dispatch(handleMarkSeen({ seenKeys: [routedNotification.chrono_key] }))
      },
    }).catch((err) => console.warn('[Notifications] click handling failed:', err))
  }

  const emptyLabel = t_all_caught_up

  // Destructive-ish (marks every notification read at once, no per-item undo) — gated behind
  // ConfirmationPopup rather than firing straight from the button, same as other bulk actions in
  // the app.
  const handleConfirmMarkAllRead = async () => {
    setIsMarkingAllRead(true)
    await dispatch(handleMarkSeen({ markAll: true }))
    setIsMarkingAllRead(false)
    setIsMarkAllConfirmOpen(false)
  }

  const node = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: notifTokens.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1400,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '1240px',
          height: '780px',
          maxWidth: '94vw',
          maxHeight: '92vh',
          background: 'white',
          borderRadius: '18px',
          boxShadow: notifShadows.modal,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <NotificationRail
          tab={tab}
          onTabChange={(nextTab) => {
            didUserChooseTabRef.current = true
            setTab(nextTab)
          }}
          onClose={onClose}
          unreadCount={unreadCount}
          allCount={inbox.items.length}
          serviceFilter={serviceFilter}
          onServiceFilterChange={setServiceFilter}
          serviceCounts={serviceCounts}
        />
        <NotificationListColumn
          active={{ ...active, items: filteredItems }}
          selectedKey={selectedKey}
          onSelect={onSelectRow}
          onLoadMore={() =>
            dispatch(tab === 'all' ? handleFetchInbox({ append: true }) : handleFetchUnread({ append: true }))
          }
          emptyLabel={emptyLabel}
          isRtl={isRtl}
          unreadCount={unreadCount}
          onMarkAllRead={() => setIsMarkAllConfirmOpen(true)}
        />
        <NotificationPreviewColumn notification={selectedNotification} onOpen={onOpenNotification} />
      </div>
      {/* ConfirmationPopup renders via a Chakra Portal, so it's not a DOM descendant of the dialog
          div above — but React still bubbles its synthetic click events through the *component*
          tree, straight up to the scrim's onClick={onClose}. Without stopping propagation here,
          clicking Cancel/Confirm inside it also closed this whole notification modal. */}
      <div onClick={(e) => e.stopPropagation()}>
        <ConfirmationPopup
          isOpen={isMarkAllConfirmOpen}
          isLoading={isMarkingAllRead}
          onCancel={() => setIsMarkAllConfirmOpen(false)}
          onConfirm={handleConfirmMarkAllRead}
          title={mark_all_seen}
          description={mark_all_seen_desc}
          cancelButtonText={no}
          confirmButtonText={yes}
        />
      </div>
    </div>
  )

  return createPortal(node, document.body)
}
